import React, { useState, useEffect } from 'react';
import {
  X, ShoppingCart, ArrowLeft, Smartphone, CreditCard, Landmark, Wallet,
  ShieldCheck, Loader2, CheckCircle2, Handshake, Clock, Info, MapPin, Plus,
} from 'lucide-react';
import { formatINR } from '@/lib/format';
import { ordersApi, negotiationsApi, addressesApi } from '@/lib/api';
import { isBulkEligible, bulkRuleHint, MAX_NEGOTIATION_DISCOUNT_PCT } from '@/lib/negotiation';

const PAYMENT_MODES = [
  { key: 'upi', label: 'UPI', icon: Smartphone },
  { key: 'card', label: 'Card', icon: CreditCard },
  { key: 'netbanking', label: 'Net Banking', icon: Landmark },
  { key: 'wallet', label: 'Wallet', icon: Wallet },
];

function hoursUntil(dateStr) {
  const ms = new Date(dateStr).getTime() - Date.now();
  return Math.max(0, Math.round(ms / (1000 * 60 * 60)));
}

// Self-contained order flow: quantity → (optional) bulk price negotiation →
// payment breakdown (platform commission + GST) → payment mode → gateway
// checkout. Talks to the backend directly so parents just need to know
// when it's done.
//
// `initialNegotiation`: pass an already-ACCEPTED negotiation (e.g. from the
// buyer's "My Negotiations" dashboard tab) to skip straight to payment at
// the agreed price/quantity.
export default function OrderModal({ listing, onClose, onSuccess, initialNegotiation }) {
  const moq = listing.moq || 1;
  const [step, setStep] = useState(initialNegotiation ? 'payment' : 'quantity');
  const [qty, setQty] = useState(initialNegotiation ? initialNegotiation.quantity : moq);
  const [qtyError, setQtyError] = useState('');
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState('upi');
  const [order, setOrder] = useState(null);
  const [payment, setPayment] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  // Negotiation state
  const [negotiation, setNegotiation] = useState(initialNegotiation || null);
  const [offeredPrice, setOfferedPrice] = useState('');
  const [negError, setNegError] = useState('');
  const [negSubmitting, setNegSubmitting] = useState(false);

  // Shipping details state
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    fullName: '', mobileNumber: '', alternativeMobileNumber: '',
    addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
  });
  const [saveAddressForFuture, setSaveAddressForFuture] = useState(true);
  const [shippingError, setShippingError] = useState('');

  useEffect(() => {
    addressesApi
      .list()
      .then((res) => {
        const addresses = res?.addresses ?? [];
        setSavedAddresses(addresses);
        const defaultAddr = addresses.find((a) => a.isDefault) || addresses[0];
        if (defaultAddr) setSelectedAddressId(defaultAddr.id);
        else setShowNewAddressForm(true);
      })
      .catch(() => setShowNewAddressForm(true))
      .finally(() => setAddressesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateNewAddress = (field, value) => setNewAddress((prev) => ({ ...prev, [field]: value }));

  const handleContinueFromShipping = () => {
    setShippingError('');
    if (!showNewAddressForm) {
      if (!selectedAddressId) { setShippingError('Select an address, or add a new one.'); return; }
      handlePayNow({ addressId: selectedAddressId });
      return;
    }
    const { fullName, mobileNumber, addressLine1, city, state, pincode } = newAddress;
    if (!fullName || !mobileNumber || !addressLine1 || !city || !state || !pincode) {
      setShippingError('Please fill in all required shipping fields.');
      return;
    }
    if (!/^\+?[1-9]\d{7,14}$/.test(mobileNumber.replace(/[\s-]/g, ''))) {
      setShippingError('Enter a valid mobile number.');
      return;
    }
    handlePayNow({ shipping: { ...newAddress, saveAddress: saveAddressForFuture && savedAddresses.length < 3 } });
  };

  const bulkEligible = isBulkEligible(listing, qty);
  const minAllowedPrice = Math.round(listing.price * (1 - MAX_NEGOTIATION_DISCOUNT_PCT / 100) * 100) / 100;
  const offeredNum = Number(offeredPrice) || 0;
  const impliedDiscount = offeredNum > 0 ? Math.round(((listing.price - offeredNum) / listing.price) * 100 * 10) / 10 : 0;

  const fetchQuote = async (negotiationId) => {
    setQuoteLoading(true);
    setError('');
    try {
      const { quote } = await ordersApi.quote({ listingId: listing.id, quantity: qty, negotiationId });
      setQuote(quote);
      setStep('payment');
    } catch (err) {
      setError(err.message || 'Could not calculate payment details.');
    } finally {
      setQuoteLoading(false);
    }
  };

  // Opened directly with an already-accepted negotiation (e.g. from the "My
  // Negotiations" dashboard tab) — fetch its payment breakdown immediately.
  React.useEffect(() => {
    if (initialNegotiation) fetchQuote(initialNegotiation.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueToPayment = async () => {
    if (qty < moq) { setQtyError(`Minimum order quantity is ${moq} units`); return; }
    if (qty > listing.quantity) { setQtyError(`Only ${listing.quantity} units available`); return; }
    fetchQuote(undefined);
  };

  const handleSubmitOffer = async () => {
    setNegError('');
    if (!offeredNum || offeredNum <= 0) { setNegError('Enter your proposed unit price.'); return; }
    if (offeredNum > listing.price) { setNegError('Your offer cannot exceed the listed price.'); return; }
    if (offeredNum < minAllowedPrice) {
      setNegError(`The maximum discount you can request is ${MAX_NEGOTIATION_DISCOUNT_PCT}% off — try ${formatINR(minAllowedPrice)} or higher.`);
      return;
    }
    setNegSubmitting(true);
    try {
      const { negotiation: created } = await negotiationsApi.create({
        listingId: listing.id,
        quantity: qty,
        offeredPrice: offeredNum,
      });
      setNegotiation(created);
      if (created.status === 'accepted') {
        // Auto-accepted by the seller's pre-set threshold — go straight to payment.
        await fetchQuote(created.id);
      } else {
        setStep('negotiate-pending');
      }
    } catch (err) {
      setNegError(err.message || 'Could not submit your offer.');
    } finally {
      setNegSubmitting(false);
    }
  };

  const handlePayNow = async (shippingPayload) => {
    setProcessing(true);
    setError('');
    try {
      const res = await ordersApi.create({
        listingId: listing.id,
        quantity: qty,
        paymentMode,
        negotiationId: negotiation?.status === 'accepted' ? negotiation.id : undefined,
        ...shippingPayload,
      });
      setOrder(res.order);
      setPayment(res.payment);
      setStep('gateway');
    } catch (err) {
      setError(err.message || 'Could not start checkout.');
    } finally {
      setProcessing(false);
    }
  };

  // Demo/mock gateway confirm — sends the well-known sentinel signature the
  // backend accepts when no real Razorpay credentials are configured (see
  // cc-backend/src/services/payment.service.js). With real credentials
  // configured, replace this with a window.Razorpay(...).open() call using
  // `payment.keyId` / `payment.gatewayOrderId` and pass through the live
  // razorpay_payment_id / razorpay_signature instead.
  const handleMockPaymentConfirm = async () => {
    setProcessing(true);
    setError('');
    try {
      const mockPaymentId = `pay_MOCK${Math.random().toString(36).slice(2, 12)}`;
      await ordersApi.verifyPayment(order.id, { paymentId: mockPaymentId, signature: 'MOCK_SIGNATURE_OK' });
      setStep('success');
      setTimeout(() => onSuccess(qty), 900);
    } catch (err) {
      setError(err.message || 'Payment verification failed.');
    } finally {
      setProcessing(false);
    }
  };

  const backFromPayment = () => setStep(negotiation ? 'negotiate-form' : 'quantity');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay">
      <div
        className="w-full max-w-md mx-4 paper-texture border-2 border-foreground ink-shadow max-h-[90vh] overflow-y-auto"
        style={{ animation: 'fadeIn 150ms ease' }}
      >
        <div className="flex items-center justify-between p-4 border-b-2 border-foreground">
          <div className="flex items-center gap-2">
            {!['quantity', 'success', 'gateway'].includes(step) && !initialNegotiation && (
              <button
                onClick={() => {
                  if (step === 'shipping') setStep('payment');
                  else if (step === 'payment') setStep(negotiation ? 'negotiate-pending' : 'quantity');
                  else setStep('quantity');
                }}
                className="p-1 hover:opacity-70 transition-opacity"
              >
                <ArrowLeft size={15} />
              </button>
            )}
            {step === 'negotiate-form' ? <Handshake size={16} style={{ color: 'var(--secondary)' }} /> : <ShoppingCart size={16} style={{ color: 'var(--primary)' }} />}
            <h3 className="font-heading text-base tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
              {step === 'quantity' && 'PLACE ORDER'}
              {step === 'negotiate-form' && 'NEGOTIATE PRICE'}
              {step === 'negotiate-pending' && 'OFFER SENT'}
              {step === 'payment' && 'PAYMENT DETAILS'}
              {step === 'shipping' && 'SHIPPING DETAILS'}
              {step === 'gateway' && 'COMPLETE PAYMENT'}
              {step === 'success' && 'ORDER CONFIRMED'}
            </h3>
          </div>
          {step !== 'gateway' && (
            <button onClick={onClose} className="p-1 hover:opacity-70 transition-opacity">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="p-4 border-b border-border" style={{ backgroundColor: 'var(--background)' }}>
          <p className="text-sm font-medium leading-snug mb-2">{listing.title}</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)' }}>
              {formatINR(listing.price)} / unit
            </span>
            <span style={{ color: 'var(--muted-foreground)' }}>·</span>
            <span style={{ color: 'var(--muted-foreground)' }}>{listing.sellerCompany}</span>
          </div>
        </div>

        {error && (
          <div className="mx-4 mt-4 px-3 py-2 border text-xs" style={{ backgroundColor: 'var(--danger-bg)', borderColor: 'var(--danger)', color: 'var(--danger)' }}>
            {error}
          </div>
        )}

        {/* ── Step: Quantity ────────────────────────────────── */}
        {step === 'quantity' && (
          <>
            <div className="p-4">
              <label className="block text-xs font-heading tracking-stamp mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
                QUANTITY TO ORDER <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                MOQ: {moq.toLocaleString('en-IN')} · Max: {listing.quantity.toLocaleString('en-IN')} units
              </p>
              <input
                type="number"
                min={moq}
                max={listing.quantity}
                value={qty}
                onChange={(e) => { setQty(Number(e.target.value)); setQtyError(''); }}
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              {qtyError && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{qtyError}</p>}
              <p className="text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>
                Item subtotal: <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(qty * listing.price)}</span>.
                Platform commission and GST are calculated on the next step.
              </p>

              {listing.negotiable && (
                <div className="mt-3 p-2.5 border" style={{ borderColor: bulkEligible ? 'var(--secondary)' : 'var(--border)', backgroundColor: bulkEligible ? 'var(--success-bg)' : 'var(--card)' }}>
                  <div className="flex items-start gap-2">
                    <Handshake size={13} style={{ color: bulkEligible ? 'var(--secondary)' : 'var(--muted-foreground)', flexShrink: 0, marginTop: '1px' }} />
                    <p className="text-xs" style={{ color: bulkEligible ? 'var(--foreground)' : 'var(--muted-foreground)' }}>
                      {bulkEligible
                        ? `This quantity qualifies for bulk price negotiation — up to ${MAX_NEGOTIATION_DISCOUNT_PCT}% off.`
                        : `Bulk price negotiation available on this listing. ${bulkRuleHint(listing)}`}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 p-4 border-t border-border">
              <div className="flex gap-3">
                <button onClick={onClose} className="btn-secondary flex-1 px-4 py-2.5 text-sm">CANCEL</button>
                <button
                  onClick={handleContinueToPayment}
                  disabled={quoteLoading}
                  className="btn-primary flex-1 px-4 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {quoteLoading ? <Loader2 size={14} className="animate-spin" /> : null}
                  {quoteLoading ? 'CALCULATING...' : 'VIEW PAYMENT DETAILS'}
                </button>
              </div>
              {listing.negotiable && bulkEligible && (
                <button
                  onClick={() => setStep('negotiate-form')}
                  className="w-full px-4 py-2.5 text-sm border-2 flex items-center justify-center gap-2"
                  style={{ borderColor: 'var(--secondary)', color: 'var(--secondary)' }}
                >
                  <Handshake size={14} /> NEGOTIATE PRICE (BULK)
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Step: Negotiate — buyer proposes a price ─────────── */}
        {step === 'negotiate-form' && (
          <>
            <div className="p-4">
              <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                Propose your price for {qty.toLocaleString('en-IN')} units. The maximum discount you can request is{' '}
                {MAX_NEGOTIATION_DISCOUNT_PCT}% off the listed price of {formatINR(listing.price)}/unit.
              </p>
              <label className="block text-xs font-heading tracking-stamp mb-1.5" style={{ fontFamily: 'var(--font-heading)' }}>
                YOUR PROPOSED UNIT PRICE (₹) <span style={{ color: 'var(--primary)' }}>*</span>
              </label>
              <input
                type="number"
                min={minAllowedPrice}
                max={listing.price}
                step="0.01"
                value={offeredPrice}
                onChange={(e) => { setOfferedPrice(e.target.value); setNegError(''); }}
                placeholder={`Min ${formatINR(minAllowedPrice)}`}
                className="input-ledger w-full px-3 py-2.5 text-sm font-mono-data"
                style={{ fontFamily: 'var(--font-mono)' }}
              />
              {negError && <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{negError}</p>}

              {offeredNum > 0 && !negError && (
                <div className="mt-3 p-2.5 border" style={{ borderColor: 'var(--border)', backgroundColor: 'var(--card)' }}>
                  <div className="flex justify-between text-xs">
                    <span style={{ color: 'var(--muted-foreground)' }}>Implied discount</span>
                    <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: impliedDiscount <= MAX_NEGOTIATION_DISCOUNT_PCT ? 'var(--secondary)' : 'var(--danger)' }}>
                      {impliedDiscount}%
                    </span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span style={{ color: 'var(--muted-foreground)' }}>Proposed order value</span>
                    <span className="font-mono-data" style={{ fontFamily: 'var(--font-mono)' }}>{formatINR(offeredNum * qty)}</span>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 mt-3 p-2.5 border" style={{ borderColor: 'var(--amber)', backgroundColor: 'var(--amber-bg)' }}>
                <Info size={13} style={{ color: 'var(--amber)', flexShrink: 0, marginTop: '1px' }} />
                <p className="text-xs" style={{ color: 'var(--foreground)' }}>
                  The seller has 24 hours to accept, counter, or reject your offer. Some sellers auto-approve offers
                  within a preset discount — you may get an instant answer.
                </p>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t border-border">
              <button onClick={() => setStep('quantity')} className="btn-secondary flex-1 px-4 py-2.5 text-sm">BACK</button>
              <button
                onClick={handleSubmitOffer}
                disabled={negSubmitting}
                className="flex-1 px-4 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                style={{ backgroundColor: 'var(--secondary)', color: 'white' }}
              >
                {negSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Handshake size={14} />}
                {negSubmitting ? 'SENDING...' : 'SEND OFFER'}
              </button>
            </div>
          </>
        )}

        {/* ── Step: Negotiate — waiting for seller ─────────────── */}
        {step === 'negotiate-pending' && negotiation && (
          <div className="p-6 flex flex-col items-center text-center gap-3">
            <Clock size={36} style={{ color: 'var(--amber)' }} />
            <p className="text-sm font-medium">Waiting for seller approval</p>
            <p className="text-xs max-w-xs" style={{ color: 'var(--muted-foreground)' }}>
              You offered {formatINR(negotiation.offeredPrice)}/unit for {negotiation.quantity.toLocaleString('en-IN')} units
              ({negotiation.discountPercent}% off). This expires in {hoursUntil(negotiation.expiresAt)}h if the seller
              doesn't respond.
            </p>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Track this offer anytime from <span className="font-medium">My Negotiations</span> in your dashboard —
              you can pay as soon as it's accepted.
            </p>
            <button onClick={onClose} className="btn-secondary px-4 py-2 text-sm mt-2">CLOSE</button>
          </div>
        )}

        {/* ── Step: Payment breakdown + mode selection ─────────── */}
        {step === 'payment' && quoteLoading && !quote && (
          <div className="p-8 flex justify-center">
            <Loader2 size={24} className="animate-spin" style={{ color: 'var(--primary)' }} />
          </div>
        )}
        {step === 'payment' && quote && (
          <>
            <div className="p-4">
              {quote.negotiated && (
                <div className="mb-3 flex items-center gap-2 px-2.5 py-2 border" style={{ borderColor: 'var(--secondary)', backgroundColor: 'var(--success-bg)' }}>
                  <Handshake size={13} style={{ color: 'var(--secondary)' }} />
                  <p className="text-xs" style={{ color: 'var(--foreground)' }}>
                    Negotiated price applied — {formatINR(quote.unitPrice)}/unit (listed at {formatINR(quote.listedPrice)}).
                  </p>
                </div>
              )}
              <div className="border" style={{ borderColor: 'var(--border)' }}>
                <BreakdownRow label={`Item Subtotal (${qty} × ${formatINR(quote.unitPrice)})`} value={formatINR(quote.subtotal)} />
                <BreakdownRow
                  label={`Platform Commission (${quote.commissionRate}%)`}
                  value={formatINR(quote.commissionAmount)}
                  hint={quote.subtotal > 100000 ? 'Deal > ₹1L → 5% rate applied' : 'Deal ≤ ₹1L → 8% rate applied'}
                />
                <BreakdownRow label={`GST on Commission (${quote.gstRate}%)`} value={formatINR(quote.gstAmount)} />
                <div className="flex justify-between items-center px-3 py-3" style={{ backgroundColor: 'var(--card)' }}>
                  <span className="text-sm font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>TOTAL PAYABLE</span>
                  <span
                    className="text-lg font-heading font-mono-data"
                    style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatINR(quote.totalPayable)}
                  </span>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-xs font-heading tracking-stamp mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  SELECT PAYMENT MODE <span style={{ color: 'var(--primary)' }}>*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_MODES.map((m) => (
                    <button
                      key={m.key}
                      onClick={() => setPaymentMode(m.key)}
                      className={`flex items-center gap-2 px-3 py-2.5 text-sm border-2 transition-colors ${paymentMode === m.key ? '' : 'hover:opacity-80'}`}
                      style={{
                        borderColor: paymentMode === m.key ? 'var(--primary)' : 'var(--border)',
                        backgroundColor: paymentMode === m.key ? 'rgba(193,64,29,0.06)' : 'transparent',
                        color: paymentMode === m.key ? 'var(--primary)' : 'var(--foreground)',
                      }}
                    >
                      <m.icon size={15} />
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-2 mt-3 p-2.5 border" style={{ borderColor: 'var(--secondary)', backgroundColor: 'var(--success-bg)' }}>
                <ShieldCheck size={13} style={{ color: 'var(--secondary)', flexShrink: 0, marginTop: '1px' }} />
                <p className="text-xs" style={{ color: 'var(--foreground)' }}>
                  Payment is held in escrow until the seller confirms fulfillment.
                </p>
              </div>
            </div>

            <div className="flex gap-3 p-4 border-t border-border">
              {!initialNegotiation && <button onClick={backFromPayment} className="btn-secondary flex-1 px-4 py-2.5 text-sm">BACK</button>}
              <button
                onClick={() => setStep('shipping')}
                className="btn-primary flex-1 px-4 py-2.5 text-sm flex items-center justify-center gap-2"
              >
                CONTINUE TO SHIPPING DETAILS
              </button>
            </div>
          </>
        )}

        {/* ── Step: Shipping details ────────────────────────────── */}
        {step === 'shipping' && (
          <>
            <div className="p-4">
              {addressesLoading ? (
                <div className="flex justify-center py-6"><Loader2 size={20} className="animate-spin" style={{ color: 'var(--primary)' }} /></div>
              ) : (
                <>
                  {savedAddresses.length > 0 && (
                    <div className="flex flex-col gap-2 mb-3">
                      <label className="block text-xs font-heading tracking-stamp" style={{ fontFamily: 'var(--font-heading)' }}>
                        DELIVER TO
                      </label>
                      {savedAddresses.map((a) => (
                        <label
                          key={a.id}
                          className="flex items-start gap-2 p-2.5 border-2 cursor-pointer"
                          style={{ borderColor: !showNewAddressForm && selectedAddressId === a.id ? 'var(--primary)' : 'var(--border)' }}
                        >
                          <input
                            type="radio"
                            checked={!showNewAddressForm && selectedAddressId === a.id}
                            onChange={() => { setSelectedAddressId(a.id); setShowNewAddressForm(false); }}
                            className="mt-0.5"
                          />
                          <div className="text-xs">
                            <p className="font-medium text-sm">
                              {a.fullName} {a.isDefault && <span style={{ color: 'var(--secondary)' }}>· Default</span>}
                            </p>
                            <p style={{ color: 'var(--muted-foreground)' }}>
                              {a.addressLine1}{a.addressLine2 ? `, ${a.addressLine2}` : ''}, {a.city}, {a.state} {a.pincode}
                            </p>
                            <p style={{ color: 'var(--muted-foreground)' }}>{a.mobileNumber}</p>
                          </div>
                        </label>
                      ))}
                      {savedAddresses.length < 3 && !showNewAddressForm && (
                        <button
                          onClick={() => setShowNewAddressForm(true)}
                          className="flex items-center gap-1.5 text-xs px-2.5 py-2 border-2 border-dashed"
                          style={{ borderColor: 'var(--border)', color: 'var(--muted-foreground)' }}
                        >
                          <Plus size={12} /> ADD A NEW ADDRESS
                        </button>
                      )}
                    </div>
                  )}

                  {showNewAddressForm && (
                    <div className="flex flex-col gap-3">
                      {savedAddresses.length > 0 && (
                        <button onClick={() => setShowNewAddressForm(false)} className="text-xs underline self-start" style={{ color: 'var(--muted-foreground)' }}>
                          ← Use a saved address instead
                        </button>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <input placeholder="Full Name *" value={newAddress.fullName} onChange={(e) => updateNewAddress('fullName', e.target.value)} className="input-ledger px-3 py-2 text-sm" />
                        <input placeholder="Mobile Number *" value={newAddress.mobileNumber} onChange={(e) => updateNewAddress('mobileNumber', e.target.value)} className="input-ledger px-3 py-2 text-sm font-mono-data" style={{ fontFamily: 'var(--font-mono)' }} />
                      </div>
                      <input placeholder="Alternative Mobile Number (optional)" value={newAddress.alternativeMobileNumber} onChange={(e) => updateNewAddress('alternativeMobileNumber', e.target.value)} className="input-ledger px-3 py-2 text-sm font-mono-data" style={{ fontFamily: 'var(--font-mono)' }} />
                      <input placeholder="Address Line 1 *" value={newAddress.addressLine1} onChange={(e) => updateNewAddress('addressLine1', e.target.value)} className="input-ledger px-3 py-2 text-sm" />
                      <input placeholder="Address Line 2 (optional)" value={newAddress.addressLine2} onChange={(e) => updateNewAddress('addressLine2', e.target.value)} className="input-ledger px-3 py-2 text-sm" />
                      <div className="grid grid-cols-3 gap-2">
                        <input placeholder="City *" value={newAddress.city} onChange={(e) => updateNewAddress('city', e.target.value)} className="input-ledger px-3 py-2 text-sm" />
                        <input placeholder="State *" value={newAddress.state} onChange={(e) => updateNewAddress('state', e.target.value)} className="input-ledger px-3 py-2 text-sm" />
                        <input placeholder="Pincode *" value={newAddress.pincode} onChange={(e) => updateNewAddress('pincode', e.target.value)} className="input-ledger px-3 py-2 text-sm font-mono-data" style={{ fontFamily: 'var(--font-mono)' }} />
                      </div>
                      {savedAddresses.length < 3 && (
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" checked={saveAddressForFuture} onChange={(e) => setSaveAddressForFuture(e.target.checked)} />
                          Save this address for future orders ({savedAddresses.length}/3 saved)
                        </label>
                      )}
                    </div>
                  )}

                  {shippingError && <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>{shippingError}</p>}
                </>
              )}
            </div>
            <div className="flex gap-3 p-4 border-t border-border">
              <button onClick={() => setStep('payment')} className="btn-secondary flex-1 px-4 py-2.5 text-sm">BACK</button>
              <button
                onClick={handleContinueFromShipping}
                disabled={processing || addressesLoading}
                className="btn-primary flex-1 px-4 py-2.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {processing ? <Loader2 size={14} className="animate-spin" /> : null}
                {processing ? 'STARTING CHECKOUT...' : `PAY ${quote ? formatINR(quote.totalPayable) : ''}`}
              </button>
            </div>
          </>
        )}

        {/* ── Step: Gateway (mock or real) ─────────────────────── */}
        {step === 'gateway' && payment && order && (
          <div className="p-4 flex flex-col items-center text-center gap-3">
            {!payment.isLiveGateway && (
              <div className="w-full px-3 py-2 border text-xs" style={{ borderColor: 'var(--amber)', backgroundColor: 'var(--amber-bg)', color: 'var(--amber)' }}>
                Demo payment gateway — no live payment provider is configured yet.
              </div>
            )}
            <div className="w-full border p-4" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs font-heading tracking-stamp mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--muted-foreground)' }}>
                AMOUNT TO PAY
              </p>
              <p className="text-2xl font-heading font-mono-data" style={{ fontFamily: 'var(--font-mono)', color: 'var(--primary)' }}>
                {formatINR(payment.amount)}
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                via {PAYMENT_MODES.find((m) => m.key === paymentMode)?.label} · Gateway order {payment.gatewayOrderId}
              </p>
            </div>
            <button
              onClick={handleMockPaymentConfirm}
              disabled={processing}
              className="btn-primary w-full px-4 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {processing ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              {processing ? 'VERIFYING PAYMENT...' : 'SIMULATE SUCCESSFUL PAYMENT'}
            </button>
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              In production this opens the real gateway checkout (Razorpay) using the key and order id above.
            </p>
          </div>
        )}

        {/* ── Step: Success ─────────────────────────────────────── */}
        {step === 'success' && (
          <div className="p-8 flex flex-col items-center text-center gap-3">
            <CheckCircle2 size={40} style={{ color: 'var(--secondary)' }} />
            <p className="text-sm font-medium">Payment confirmed. Your order has been sent to the seller.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function BreakdownRow({ label, value, hint }) {
  return (
    <div className="flex justify-between items-start px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
      <div>
        <p className="text-xs" style={{ color: 'var(--foreground)' }}>{label}</p>
        {hint && <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{hint}</p>}
      </div>
      <span className="text-xs font-mono-data whitespace-nowrap" style={{ fontFamily: 'var(--font-mono)' }}>{value}</span>
    </div>
  );
}
